import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, MapPinIcon, MoonIcon, SunIcon } from 'lucide-react';
import { CourseCalendar } from '../components/CourseCalendar';
import { currentCourse, futureCourses, pastCourses } from '../data/courses';
import { futurePalette, pastPalette } from '../data/courseThemes';
import type { CalendarPalette } from '../data/courseThemes';
import { countSessions, formatRange } from '../utils/calendar';
import type { Course } from '../types/course';
const REGISTRATION_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdN1pDWRqjo_eykjFE7VECqifWUKuHBRr2Fq-LVvCOnP6_lwg/viewform?usp=publish-editor';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.65,
    ease: 'easeOut'
  }
};
function CourseHeading({
  course,
  palette
}: {
  course: Course;
  palette?: CalendarPalette;
}) {
  return <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2 mb-6" data-id="element-68" data-mp="1uu8sr0n99pke">
      <h3 className={`text-2xl font-bold ${palette ? '' : 'text-mountain-darker'}`} style={palette ? {
      color: palette.heading
    } : undefined} data-id="element-69" data-mp="1pwh0g5ip81fo">
        {course.group}
      </h3>
      <span className={`inline-flex items-center gap-1.5 text-sm ${palette ? '' : 'text-mountain-dark/60'}`} style={palette ? {
      color: palette.muted
    } : undefined} data-id="element-70" data-mp="1bwfr01odblol">
        <MapPinIcon className="w-3.5 h-3.5" data-id="element-71" data-mp="1fyssgdw30xfk" />
        {course.location}
      </span>
      {course.note && <span className={`text-xs uppercase tracking-wider font-semibold px-3 py-1 rounded-full ${palette ? '' : 'bg-gold/15 text-gold-dark'}`} style={palette ? {
      background: palette.badgeBg,
      color: palette.badgeText
    } : undefined} data-id="element-72" data-mp="2wcsq1i7ggazn">
          {course.note}
        </span>}
    </div>;
}
export function CoursesPage({
  mode,
  toggleMode,
  onBack
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  onBack: () => void;
}) {
  const [openPastId, setOpenPastId] = useState<string | null>(null);
  return <main className="w-full bg-sand-lightest min-h-screen" data-id="element-73" data-mp="16bn31wxiy1ok">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50" style={{
      background: 'rgba(var(--mountain-darker), 0.96)',
      backdropFilter: 'blur(12px)'
    }} data-id="element-74" data-mp="1acgs4tmpdmj1">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-75" data-mp="1k2id3cjreotq">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-76" data-mp="mmuq7yey4t7c">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-77" data-mp="k0bu6kqv1c00" />
            Į pradžią
          </button>
          <span className="font-accent text-[#F72585] text-[26px] font-[500] hidden sm:block" data-id="element-78" data-mp="f3lfa0qskmg6">
            tavo būsena
          </span>
          <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-79" data-mp="a5jy5lcxex7c">
            {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-80" data-mp="3sh6np9lrpk1f" /> : <SunIcon className="w-5 h-5" data-id="element-81" data-mp="3nidaliae7v1s" />}
          </button>
        </div>
      </header>

      {/* ── PAGE INTRO ── */}
      <section className="pt-20 pb-12 px-4 bg-sand-light" data-id="element-82" data-mp="3ik233e7klwmq">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-83" data-mp="3ihfxhk48pr75">
          <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-84" data-mp="3egvw24cmw1f7">
            Visos grupės vienoje vietoje
          </p>
          <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
          background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
        }} data-id="element-85" data-mp="1dffvkg71zeo0" />
          <h1 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-5" data-id="element-86" data-mp="13ouzazeuqm89">
            Kursų kalendorius
          </h1>
          <p className="text-lg text-mountain-dark leading-relaxed font-[300]" data-id="element-87" data-mp="18mmwa7ucsyjb">
            Artėjančio kurso tvarkaraštis ir būsimų grupių datos, kad galėtum pasirinkti sau tinkamiausią laiką.
          </p>
        </motion.div>
      </section>

      {/* ── CURRENT COURSE (baltai rožinis, su auksu) ── */}
      <section className="py-20 px-4 bg-sand-lightest" data-id="element-88" data-mp="kt50fl27u5yl">
        <div className="max-w-6xl mx-auto" data-id="element-89" data-mp="pqob6azpg0ls">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-90" data-mp="prpx5ai9e7ia">
            <p className="text-xs uppercase tracking-widest font-semibold text-gold-dark mb-3" data-id="element-91" data-mp="2f5cnkc8apijt">
              Registracija atvira
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-mountain-darker" data-id="element-92" data-mp="2u7l4ap4edw3y">
              Artėjantis kursas
            </h2>
          </motion.div>

          <CourseHeading course={currentCourse} data-id="element-93" data-mp="2pab4qgnao42e" />
          <CourseCalendar course={currentCourse} data-id="element-94" data-mp="32zwm5mlg4b8b" />

          <motion.div className="flex justify-center mt-10" {...fadeInUp} data-id="element-95" data-mp="331fyx37w845c">
            <motion.a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-white px-11 py-[15px] rounded-full text-base font-semibold shadow-xl inline-block" style={{
            background: '#1B1B4B'
          }} data-id="element-96" data-mp="2y8dd5h9cxyzp">
              Registruotis į{' '}
              {currentCourse.group.replace('grupė', 'grupę').toLowerCase()}
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ── FUTURE COURSES ── */}
      <section className="py-20 px-4" style={{
      background: futurePalette.sectionBg
    }} data-id="element-97" data-mp="3rnhghi942bqc">
        <div className="max-w-6xl mx-auto" data-id="element-98" data-mp="5g124z56vjoe">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-99" data-mp="5h2bmz6k3y40">
            <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{
            color: futurePalette.accent
          }} data-id="element-100" data-mp="9hvzxtnwvexf">
              Planuok iš anksto
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{
            color: futurePalette.heading
          }} data-id="element-101" data-mp="j8qsffrr3arb">
              Būsimi kursai
            </h2>
          </motion.div>

          <div className="space-y-16" data-id="element-102" data-mp="x76uuuikyf3n">
            {futureCourses.map(course => <div key={course.id} data-id="element-103" data-mp="x544289lshxv">
                <CourseHeading course={course} palette={futurePalette} data-id="element-104" data-mp="2hpr74jkf2sf9" />
                <CourseCalendar course={course} palette={futurePalette} showStats={false} data-id="element-105" data-mp="2dnnrdiqpt1o6" />
              </div>)}
          </div>
        </div>
      </section>

      {/* ── PAST COURSES ── */}
      <section className="py-20 px-4" style={{
      background: pastPalette.sectionBg
    }} data-id="element-106" data-mp="23razmqeqcxw2">
        <div className="max-w-4xl mx-auto" data-id="element-107" data-mp="2we5gldcc9f4s">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-108" data-mp="2wf6wbv4aupsu">
            <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{
            color: pastPalette.muted
          }} data-id="element-109" data-mp="2rn5mefultb5z">
              Archyvas
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{
            color: pastPalette.heading
          }} data-id="element-110" data-mp="3k4rn306p2rco">
              Praėję kursai
            </h2>
          </motion.div>

          <motion.div className="border rounded-2xl overflow-hidden" style={{
          background: pastPalette.cardBg,
          borderColor: pastPalette.cardBorder
        }} {...fadeInUp} data-id="element-111" data-mp="2nnc1o68xapf3">
            {pastCourses.map((course, i) => {
            const isOpen = openPastId === course.id;
            return <div key={course.id} style={i > 0 ? {
              borderTop: `1px solid ${pastPalette.cardBorder}`
            } : undefined} data-id="element-112" data-mp="36gg0e60zzsgk" data-mpi={i}>
                  <button onClick={() => setOpenPastId(isOpen ? null : course.id)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 transition-colors duration-200" style={{
                background: isOpen ? pastPalette.sectionBg : 'transparent'
              }} data-id="element-113" data-mp="32ci03rzxsblx">
                    <span className="min-w-0" data-id="element-114" data-mp="1sgbhxeyfsnre">
                      <span className="block text-base font-semibold" style={{
                    color: pastPalette.heading
                  }} data-id="element-115" data-mp="1sf08ozm82nsw">
                        {course.group} · {new Date(course.start).getFullYear()}
                      </span>
                      <span className="block text-sm mt-0.5" style={{
                    color: pastPalette.muted
                  }} data-id="element-116" data-mp="19hgznvzrl56h">
                        {formatRange(course.start, course.end)} ·{' '}
                        {countSessions(course.start, course.end, course.weekdays)}{' '}
                        užsiėmimai · {course.note}
                      </span>
                    </span>
                    <motion.span animate={{
                  rotate: isOpen ? 180 : 0
                }} transition={{
                  duration: 0.2,
                  ease: [0.23, 1, 0.32, 1]
                }} className="flex-shrink-0" style={{
                  color: pastPalette.accent
                }} data-id="element-117" data-mp="231p86wcu5kc1">
                      <ChevronDownIcon className="w-5 h-5" data-id="element-118" data-mp="1feh1qb3o858f" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false} data-id="element-119" data-mp="1acrqovgg7rkc">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" style={{
                  background: pastPalette.sectionBg
                }} data-id="element-120" data-mp="15ezxzz7zc1f3">
                        <div className="px-6 py-8" data-id="element-121" data-mp="33af2z3ajnznt">
                          <CourseCalendar course={course} palette={pastPalette} showStats={false} data-id="element-122" data-mp="382q4ubouhvj0" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-mountain-darker" data-id="element-123" data-mp="23hrvnquz4knz">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-124" data-mp="2ha7wqm4be8m9">
          <span className="font-accent text-[#F72585] text-[26px] font-[500]" data-id="element-125" data-mp="2cdhcrnpe2svk">
            tavo būsena
          </span>
          <p className="text-white/50 text-sm" data-id="element-126" data-mp="1omvhyy4eb53i">© 2026 tavo būsena.</p>
          <button onClick={onBack} className="text-white/70 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-127" data-mp="1km1ug5uv7sb5">
            Į pradžią
          </button>
        </div>
      </footer>
    </main>;
}